import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../../api/endpoints/settings';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

const HEADER_STYLES = [
  { value: 'default', labelKey: 'settingsNavbar.styleDefault' },
  { value: 'minimal', labelKey: 'settingsNavbar.styleMinimal' },
  { value: 'centered', labelKey: 'settingsNavbar.styleCentered' },
] as const;

interface Props { settings?: StoreSettings; storeId: string; }

export function NavbarTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [headerStyle, setHeaderStyle] = useState('default');
  const [showCurrencySelector, setShowCurrencySelector] = useState(true);
  const [showSearchIcon, setShowSearchIcon] = useState(true);
  const [showUserIcon, setShowUserIcon] = useState(true);
  const [showCartIcon, setShowCartIcon] = useState(true);
  const [showCartCount, setShowCartCount] = useState(true);

  useEffect(() => {
    if (settings) {
      const hs = settings.headerSettings;
      if (hs) {
        setShowCurrencySelector(hs.showCurrencySelector ?? true);
        setShowSearchIcon(hs.showSearchIcon ?? true);
        setShowUserIcon(hs.showUserIcon ?? true);
        setShowCartIcon(hs.showCartIcon ?? true);
        setShowCartCount(hs.showCartCount ?? true);
      }
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: () =>
      settingsApi.updateHeaderSettings(storeId, {
        showCurrencySelector,
        showSearchIcon,
        showUserIcon,
        showCartIcon,
        showCartCount,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header Style */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
        <h2 className="text-base font-semibold mb-4">{t('settingsNavbar.headerStyle', 'Header Style')}</h2>
        <p className="text-sm text-text-muted mb-4">{t('settingsNavbar.headerStyleNote', 'Header style is controlled via the visual editor on the storefront.')}</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {HEADER_STYLES.map((style) => (
            <button
              key={style.value}
              type="button"
              onClick={() => setHeaderStyle(style.value)}
              className={`rounded-xl border-2 p-4 text-center transition-colors ${
                headerStyle === style.value
                  ? 'border-accent bg-accent/5'
                  : 'border-border-default hover:border-accent/50'
              }`}
            >
              {/* Visual preview */}
              <div className="mb-3 rounded-lg bg-bg-tertiary p-2">
                {style.value === 'default' && (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="h-2 w-8 rounded-full bg-accent/60" />
                      <div className="flex gap-1">
                        <div className="h-2 w-4 rounded-full bg-text-muted/30" />
                        <div className="h-2 w-4 rounded-full bg-text-muted/30" />
                        <div className="h-2 w-4 rounded-full bg-text-muted/30" />
                      </div>
                      <div className="flex gap-1">
                        <div className="h-2 w-2 rounded-full bg-text-muted/30" />
                        <div className="h-2 w-2 rounded-full bg-text-muted/30" />
                      </div>
                    </div>
                  </div>
                )}
                {style.value === 'minimal' && (
                  <div className="flex items-center justify-between">
                    <div className="h-2 w-6 rounded-full bg-accent/60" />
                    <div className="flex gap-1">
                      <div className="h-2 w-2 rounded-full bg-text-muted/30" />
                      <div className="h-2 w-2 rounded-full bg-text-muted/30" />
                    </div>
                  </div>
                )}
                {style.value === 'centered' && (
                  <div className="space-y-1">
                    <div className="flex justify-center">
                      <div className="h-2 w-8 rounded-full bg-accent/60" />
                    </div>
                    <div className="flex justify-center gap-1">
                      <div className="h-1.5 w-4 rounded-full bg-text-muted/30" />
                      <div className="h-1.5 w-4 rounded-full bg-text-muted/30" />
                      <div className="h-1.5 w-4 rounded-full bg-text-muted/30" />
                    </div>
                  </div>
                )}
              </div>
              <p className="text-sm font-medium">{t(style.labelKey, style.value)}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Display Options */}
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settingsNavbar.displayOptions', 'Display Options')}</h2>
        <Toggle
          label={t('settingsNavbar.showCurrencySelector', 'Currency Selector')}
          description={t('settingsNavbar.showCurrencySelectorDesc', 'Show currency conversion dropdown in the header')}
          checked={showCurrencySelector}
          onChange={setShowCurrencySelector}
        />
        <Toggle
          label={t('settingsNavbar.showSearchIcon', 'Search Icon')}
          description={t('settingsNavbar.showSearchIconDesc', 'Show search icon in the header')}
          checked={showSearchIcon}
          onChange={setShowSearchIcon}
        />
        <Toggle
          label={t('settingsNavbar.showUserIcon', 'User Icon')}
          description={t('settingsNavbar.showUserIconDesc', 'Show user/login icon in the header')}
          checked={showUserIcon}
          onChange={setShowUserIcon}
        />
        <Toggle
          label={t('settingsNavbar.showCartIcon', 'Cart Icon')}
          description={t('settingsNavbar.showCartIconDesc', 'Show shopping cart icon in the header')}
          checked={showCartIcon}
          onChange={setShowCartIcon}
        />
        <Toggle
          label={t('settingsNavbar.showCartCount', 'Cart Item Count')}
          description={t('settingsNavbar.showCartCountDesc', 'Show the number of items on the cart icon')}
          checked={showCartCount}
          onChange={setShowCartCount}
        />
      </section>

      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>{t('common.save')}</Button>
      </div>
    </div>
  );
}
